/**
 * Super Admin → Tipo de usuario.
 *
 * El catálogo de puestos por giro. Los 5 roles del sistema describen JERARQUÍA
 * (dueño, administrador, gerente…) y eso es igual en cualquier negocio; lo que
 * cambia por giro es el OFICIO: un restaurante contrata meseros y anfitriones,
 * un taller asesores de servicio y mecánicos, y ninguno de los dos se llama
 * «Vendedor».
 *
 * El catálogo lo escribe la plataforma y no cada negocio: así se puede traer una
 * plantilla sensata por giro. Si cada cliente inventara sus propios nombres no
 * habría nada que precargar ni con qué comparar entre clientes.
 *
 * Cada tipo lleva un ROL BASE de los 5. No es burocracia: todo el código que ya
 * existe lee el rol, así que el rol base es lo que hace que un «Mesero» funcione
 * en las partes del sistema que no saben qué es un mesero. También es el respaldo
 * si el tipo se desactiva.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  BadgeCheck, Plus, Pencil, Trash2, Loader2, Info, X, Save, EyeOff, Eye, Package,
} from 'lucide-react';

const ICONO_DE_NIVEL = { ninguno: EyeOff, lectura: Eye, escritura: Pencil };
const NOMBRE_DE_NIVEL = { ninguno: 'Sin acceso', lectura: 'Solo consultar', escritura: 'Consultar y cambiar' };

const VACIO = {
  vertical: '', clave: '', nombre: '', descripcion: '', rol_base: 'tenant_user',
  permisos: {}, entitlement_requerido: '', addon_id: '', activo: true, orden: 0,
};

export default function UserTypes() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ grupos: [], niveles: [], roles_base: [] });
  const [verticales, setVerticales] = useState([]);
  const [addons, setAddons] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null);   // objeto del formulario o null
  const [guardando, setGuardando] = useState(false);
  const [filtro, setFiltro] = useState('');

  const cargar = useCallback(() => {
    setCargando(true);
    Promise.all([
      api.get('/admin/tipos-de-usuario'),
      api.get('/admin/verticals').catch(() => ({ data: [] })),
      api.get('/admin/addons').catch(() => ({ data: [] })),
    ])
      .then(([tipos, verts, adds]) => {
        setItems(tipos.data?.items || []);
        setMeta({
          grupos: tipos.data?.grupos || [],
          niveles: tipos.data?.niveles || [],
          roles_base: tipos.data?.roles_base || [],
        });
        const listaVerts = Array.isArray(verts.data) ? verts.data : (verts.data?.items || []);
        setVerticales(listaVerts);
        const listaAdds = Array.isArray(adds.data) ? adds.data : (adds.data?.items || []);
        setAddons(listaAdds);
      })
      .catch(() => toast.error('No se pudo cargar el catálogo de tipos de usuario'))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const porVertical = useMemo(() => {
    const agrupado = {};
    items
      .filter(t => !filtro || t.vertical === filtro)
      .forEach(t => { (agrupado[t.vertical] = agrupado[t.vertical] || []).push(t); });
    return agrupado;
  }, [items, filtro]);

  const nombreVertical = (clave) => {
    const v = verticales.find(x => (x.key || x.clave) === clave);
    return v?.label || v?.name || v?.nombre || clave;
  };

  const guardar = async () => {
    if (!editando) return;
    if (!editando.nombre?.trim()) return toast.error('Ponle un nombre al tipo de usuario');
    if (!editando.id && !editando.vertical) return toast.error('Elige el giro de negocio');
    if (!editando.id && !editando.clave?.trim()) return toast.error('Ponle una clave al tipo');

    setGuardando(true);
    try {
      const cuerpo = {
        nombre: editando.nombre.trim(),
        descripcion: editando.descripcion || null,
        rol_base: editando.rol_base,
        permisos: editando.permisos || {},
        entitlement_requerido: editando.entitlement_requerido || null,
        addon_id: editando.addon_id || null,
        activo: !!editando.activo,
        orden: Number(editando.orden) || 0,
      };
      if (editando.id) {
        await api.put(`/admin/tipos-de-usuario/${editando.id}`, cuerpo);
        toast.success('Tipo de usuario actualizado');
      } else {
        await api.post('/admin/tipos-de-usuario', {
          ...cuerpo, vertical: editando.vertical, clave: editando.clave.trim(),
        });
        toast.success('Tipo de usuario creado');
      }
      setEditando(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (t) => {
    if (!window.confirm(
      `¿Quitar «${t.nombre}» del catálogo?\n\n` +
      'Si hay personas con este tipo en algún negocio, no se borra: se desactiva, ' +
      'para que nadie pierda su acceso de golpe.')) return;
    try {
      const r = await api.delete(`/admin/tipos-de-usuario/${t.id}`);
      toast.success(r.data?.mensaje || (r.data?.accion === 'borrado' ? 'Tipo borrado' : 'Tipo desactivado'));
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo quitar');
    }
  };

  const cambiarPermiso = (modulo, nivel) => {
    setEditando(prev => {
      const permisos = { ...(prev.permisos || {}) };
      // Quitar la clave es distinto de ponerla en «ninguno»: sin clave, el tipo
      // hereda el valor de su rol base. Esa diferencia importa y se ofrece.
      if (nivel === '') delete permisos[modulo];
      else permisos[modulo] = nivel;
      return { ...prev, permisos };
    });
  };

  return (
    <div className="p-6 max-w-6xl" data-testid="admin-user-types">
      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <div className="flex items-center gap-3">
          <BadgeCheck className="w-6 h-6 text-[#F72585]" />
          <h1 className="text-2xl font-bold text-[#0F172A]">Tipo de usuario</h1>
        </div>
        <button
          type="button"
          onClick={() => setEditando({ ...VACIO, vertical: filtro || '' })}
          data-testid="nuevo-tipo"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#F72585] hover:opacity-90"
        >
          <Plus className="w-4 h-4" /> Nuevo tipo
        </button>
      </div>
      <p className="text-sm text-[#64748B] mb-5 max-w-3xl">
        Los puestos que cada giro de negocio puede usar. Un restaurante contrata meseros y
        anfitriones; un taller, asesores de servicio. El negocio no los inventa: elige de aquí
        y decide qué puede hacer cada uno en sus propios permisos.
      </p>

      <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg p-3 mb-6 flex gap-2.5">
        <Info className="w-4 h-4 text-[#64748B] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-[#64748B] leading-relaxed space-y-1">
          <p>
            <strong className="text-[#0F172A]">El rol base no es un trámite.</strong> Todo el
            sistema que ya existe entiende los 5 roles; el rol base es lo que hace que un
            «Mesero» funcione en las partes que no saben qué es un mesero, y es su respaldo si
            algún día desactivas el tipo.
          </p>
          <p>
            <strong className="text-[#0F172A]">El vínculo a un paquete es opcional.</strong> Si lo
            dejas vacío, el tipo está disponible para cualquier negocio de ese giro. Si lo pones,
            solo lo verán los negocios cuyo plan o complemento lo incluya.
          </p>
        </div>
      </div>

      {verticales.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          <button
            type="button" onClick={() => setFiltro('')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${!filtro
              ? 'bg-[#F72585] text-white border-[#F72585]'
              : 'bg-white text-[#0F172A] border-[#E2E8F0] hover:bg-[#F8F9FA]'}`}
          >Todos los giros</button>
          {verticales.map(v => {
            const clave = v.key || v.clave;
            return (
              <button
                key={clave} type="button" onClick={() => setFiltro(clave)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${filtro === clave
                  ? 'bg-[#F72585] text-white border-[#F72585]'
                  : 'bg-white text-[#0F172A] border-[#E2E8F0] hover:bg-[#F8F9FA]'}`}
              >{v.label || v.name || v.nombre || clave}</button>
            );
          })}
        </div>
      )}

      {cargando && (
        <div className="flex items-center gap-2 text-sm text-[#64748B] py-10 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      )}

      {!cargando && items.length === 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
          <p className="text-sm text-[#64748B]">
            Todavía no hay tipos de usuario en el catálogo. Los negocios seguirán usando los 5
            puestos base (Dueño, Administrador, Gerente, Vendedor, Cajero) hasta que agregues
            alguno aquí.
          </p>
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(porVertical).map(([vertical, tipos]) => (
          <div key={vertical}>
            <h2 className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-3">
              {nombreVertical(vertical)}
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {tipos.map(t => (
                <div key={t.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[#0F172A]">{t.nombre}</span>
                        {!t.activo && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#F8F9FA] text-[#64748B] border border-[#E2E8F0]">
                            desactivado
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#64748B] mt-0.5">
                        Funciona como <strong>{t.nombre_rol_base}</strong> · clave <code>{t.clave}</code>
                      </p>
                      {t.descripcion && (
                        <p className="text-xs text-[#64748B] mt-1.5">{t.descripcion}</p>
                      )}
                      <p className="text-xs text-[#64748B] mt-1.5">
                        {Object.keys(t.permisos || {}).length === 0
                          ? 'Sin permisos propios: hereda todo de su rol base.'
                          : `${Object.keys(t.permisos).length} módulos con permiso propio.`}
                      </p>
                      {(t.entitlement_requerido || t.addon_id) && (
                        <p className="text-xs text-[#64748B] mt-1.5 inline-flex items-center gap-1">
                          <Package className="w-3 h-3" /> Requiere paquete
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        type="button" title="Editar"
                        onClick={() => setEditando({
                          ...t, descripcion: t.descripcion || '',
                          entitlement_requerido: t.entitlement_requerido || '',
                          addon_id: t.addon_id || '',
                        })}
                        className="p-2 rounded-lg text-[#64748B] hover:bg-[#F8F9FA]"
                      ><Pencil className="w-4 h-4" /></button>
                      <button
                        type="button" title="Quitar del catálogo"
                        onClick={() => borrar(t)}
                        className="p-2 rounded-lg text-[#64748B] hover:bg-[#F8F9FA]"
                      ><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Formulario. Diálogo propio y no Radix para no arrastrar el portal en una
          pantalla de Super Admin que no lo usa en ningún otro sitio. */}
      {editando && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl border border-[#E2E8F0] w-full max-w-2xl my-8" data-testid="form-tipo">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#0F172A]">
                {editando.id ? `Editar «${editando.nombre}»` : 'Nuevo tipo de usuario'}
              </h3>
              <button type="button" onClick={() => setEditando(null)}
                      className="p-1.5 rounded-lg text-[#64748B] hover:bg-[#F8F9FA]">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Giro de negocio</label>
                  <select
                    value={editando.vertical}
                    disabled={!!editando.id}
                    onChange={e => setEditando({ ...editando, vertical: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white text-[#0F172A] disabled:opacity-60"
                  >
                    <option value="">Elige un giro…</option>
                    {verticales.map(v => {
                      const clave = v.key || v.clave;
                      return <option key={clave} value={clave}>{v.label || v.name || v.nombre || clave}</option>;
                    })}
                  </select>
                  {editando.id && (
                    <p className="text-[11px] text-[#64748B] mt-1">
                      El giro no se cambia: hay personas y permisos apuntando a este tipo.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Funciona como</label>
                  <select
                    value={editando.rol_base}
                    onChange={e => setEditando({ ...editando, rol_base: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white text-[#0F172A]"
                  >
                    {(meta.roles_base || []).map(r => (
                      <option key={r.clave} value={r.clave}>{r.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Nombre visible</label>
                  <input
                    value={editando.nombre}
                    onChange={e => setEditando({ ...editando, nombre: e.target.value })}
                    placeholder="Mesero, Asesor de servicio…"
                    className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white text-[#0F172A]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Clave interna</label>
                  <input
                    value={editando.clave}
                    disabled={!!editando.id}
                    onChange={e => setEditando({ ...editando, clave: e.target.value })}
                    placeholder="mesero"
                    className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white text-[#0F172A] disabled:opacity-60"
                  />
                  <p className="text-[11px] text-[#64748B] mt-1">
                    {editando.id
                      ? 'La clave no se cambia una vez creada.'
                      : 'Sin espacios ni acentos. No puede empezar por «tenant_».'}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">
                  Para qué sirve este puesto <span className="font-normal text-[#64748B]">(opcional)</span>
                </label>
                <input
                  value={editando.descripcion}
                  onChange={e => setEditando({ ...editando, descripcion: e.target.value })}
                  placeholder="Atiende mesas y toma pedidos. No cobra."
                  className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white text-[#0F172A]"
                />
                <p className="text-[11px] text-[#64748B] mt-1">
                  Esto lo lee el dueño del negocio al elegir el puesto. Vale la pena escribirlo.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">
                    Requiere este complemento <span className="font-normal text-[#64748B]">(opcional)</span>
                  </label>
                  <select
                    value={editando.addon_id || ''}
                    onChange={e => setEditando({ ...editando, addon_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white text-[#0F172A]"
                  >
                    <option value="">Disponible para todos</option>
                    {addons.map(a => (
                      <option key={a.id} value={a.id}>{a.name || a.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end gap-4">
                  <label className="inline-flex items-center gap-2 text-sm text-[#0F172A]">
                    <input
                      type="checkbox" checked={!!editando.activo}
                      onChange={e => setEditando({ ...editando, activo: e.target.checked })}
                    />
                    Se ofrece a los negocios
                  </label>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-[#0F172A] mb-1">Permisos con los que nace</p>
                <p className="text-[11px] text-[#64748B] mb-3">
                  Lo que dejes en blanco lo hereda de su rol base. El negocio puede ajustar todo
                  esto después en su propia pantalla de permisos.
                </p>
                <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
                  {(meta.grupos || []).map(g => (
                    <div key={g.grupo}>
                      <p className="text-[11px] uppercase tracking-widest text-[#64748B] font-semibold mb-2">
                        {g.grupo}
                      </p>
                      <div className="space-y-1.5">
                        {g.modulos.map(m => {
                          const valor = (editando.permisos || {})[m.clave] ?? '';
                          return (
                            <div key={m.clave} className="flex items-center justify-between gap-3">
                              <span className="text-xs text-[#0F172A] truncate" title={m.ayuda}>
                                {m.etiqueta}
                              </span>
                              <select
                                value={valor}
                                onChange={e => cambiarPermiso(m.clave, e.target.value)}
                                className="px-2 py-1 rounded border border-[#E2E8F0] text-xs bg-white text-[#0F172A] flex-shrink-0"
                              >
                                <option value="">Como su rol base</option>
                                {(meta.niveles || []).map(n => (
                                  <option key={n} value={n}>{NOMBRE_DE_NIVEL[n] || n}</option>
                                ))}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#E2E8F0]">
              <button type="button" onClick={() => setEditando(null)} disabled={guardando}
                      className="px-3 py-2 rounded-lg text-sm font-medium text-[#64748B] hover:bg-[#F8F9FA]">
                Cancelar
              </button>
              <button type="button" onClick={guardar} disabled={guardando} data-testid="guardar-tipo"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#F72585] hover:opacity-90 disabled:opacity-60">
                {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
